<template>
  <section class="admin-detail" v-loading="loading">
    <el-row class="header">
      <el-col :span="16">
        <el-page-header @back="cancel" :content="title"></el-page-header>
      </el-col>
      <el-col :span="8" style="text-align: right">
        <el-button v-if="!isNew" type="danger" plain @click="remove">{{ $t('admin.delete') }}</el-button>
        <el-button type="primary" :loading="saving" @click="save">{{ $t('admin.save') }}</el-button>
      </el-col>
    </el-row>

    <el-form ref="form" :model="admin" :rules="rules" label-width="140px">
      <el-form-item :label="$t('admin.firstName')" prop="firstName">
        <el-input v-model.trim="admin.firstName" :disabled="!isNew"></el-input>
      </el-form-item>
      <el-form-item :label="$t('admin.lastName')" prop="lastName">
        <el-input v-model.trim="admin.lastName" :disabled="!isNew"></el-input>
      </el-form-item>
      <el-form-item :label="$t('admin.email')" prop="email">
        <el-input v-model.trim="admin.email" :disabled="!isNew"></el-input>
      </el-form-item>
      <el-form-item :label="$t('admin.roles')" prop="roles">
        <el-select v-model="admin.roles" multiple :placeholder="$t('admin.rolesPlaceholder')">
          <el-option v-for="role in roles" :key="role" :label="role" :value="role"></el-option>
        </el-select>
      </el-form-item>
    </el-form>
  </section>
</template>

<script>
import AdminService from '@/services/AdminService';

export default {
  name: 'admin-detail',
  data() {
    return {
      loading: true,
      saving: false,
      roles: [],
      admin: {
        firstName: '', lastName: '', email: '', roles: [],
      },
    };
  },
  computed: {
    isNew() {
      return this.$route.name === 'admin-new';
    },
    title() {
      return this.isNew ? this.$t('admin.new') : `${this.admin.firstName} ${this.admin.lastName}`;
    },
    // Computed so the validation messages follow the active locale.
    rules() {
      return {
        firstName: [{ required: true, message: this.$t('admin.error.firstNameRequired'), trigger: 'blur' }],
        lastName: [{ required: true, message: this.$t('admin.error.lastNameRequired'), trigger: 'blur' }],
        email: [{
          required: true, type: 'email', message: this.$t('admin.error.emailInvalid'), trigger: 'blur',
        }],
        roles: [{
          required: true, type: 'array', min: 1, message: this.$t('admin.error.rolesRequired'), trigger: 'change',
        }],
      };
    },
  },
  async created() {
    try {
      // The roles the server accepts are the single source of truth for the multi-select.
      this.roles = await AdminService.roles();

      if (!this.isNew) {
        this.admin = await AdminService.getById(this.$route.params.id);
      }
    } catch (e) {
      this.$message.error(e.message || this.$t('admin.message.loadFailed'));
    } finally {
      this.loading = false;
    }
  },
  methods: {
    cancel() {
      this.$router.back();
    },
    async validate() {
      try {
        return await this.$refs.form.validate();
      } catch (e) {
        return false;
      }
    },
    async save() {
      if (!await this.validate()) return;

      const payload = {
        firstName: this.admin.firstName,
        lastName: this.admin.lastName,
        email: this.admin.email,
        roles: this.admin.roles,
      };

      this.saving = true;

      try {
        if (this.isNew) {
          // Assign before navigating: the route change reuses this component instance without re-running
          // `created()`, so `admin.id` has to come from the response.
          this.admin = await AdminService.create(payload);

          this.$router.push({ name: 'admin-detail', params: { id: this.admin.id } });
        } else {
          this.admin = await AdminService.update(this.admin.id, payload);
        }

        this.$message.success(this.$t('admin.message.successfulSave'));
      } catch (e) {
        this.$message.error(e.message || this.$t('admin.message.failedToSave'));
      } finally {
        this.saving = false;
      }
    },
    remove() {
      const name = `${this.admin.firstName} ${this.admin.lastName}`;

      this.$confirm(this.$t('admin.message.deleteConfirmation', { name }), this.$t('admin.delete'), {
        confirmButtonText: this.$t('admin.delete'),
        type: 'warning',
      })
        .then(async () => {
          try {
            await AdminService.delete(this.admin.id);

            this.$message.success(this.$t('admin.message.deleteSuccess'));
            this.$router.replace({ name: 'admin-list' });
          } catch (e) {
            this.$message.error(e.message || this.$t('admin.message.deleteFail'));
          }
        })
        .catch(() => { });
    },
  },
};
</script>

<style lang="scss" scoped>
.admin-detail {
  padding: 20px;

  .header {
    margin-bottom: 20px;
  }
}
</style>
